import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader, Loading } from "@/components/common/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CHART_GRID, CHART_TICK, CHART_TOOLTIP, CHART_CURSOR } from "@/lib/chart";

function Row({ label, value, bold, accent, indent }) {
  return (
    <div className={`flex items-center justify-between border-b border-border py-3 ${indent ? "pl-6" : ""}`}>
      <span className={bold ? "font-display font-bold" : "text-muted-foreground"}>{label}</span>
      <span className={`tabular ${bold ? "font-display text-lg font-bold" : "font-medium"} ${accent || ""}`}>{money(value)}</span>
    </div>
  );
}

export default function PnL() {
  const { user } = useAuth();
  const { outletOptions, outletName } = useMeta();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";
  const [outlet, setOutlet] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const { data } = await api.get("/pnl", { params: { outlet_id: outlet || undefined, from_date: from || undefined, to_date: to || undefined } }); setD(data); }
      finally { setLoading(false); }
    };
    load();
  }, [outlet, from, to]);

  return (
    <div className="space-y-6">
      <PageHeader title="Profit & Loss" subtitle={`Net Sales − COGS = Gross Profit − Operating Expenses = Net Profit · ${outletName(outlet)}`}>
        {isSuper && (
          <Select value={outlet || "all"} onValueChange={(v) => setOutlet(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="pnl-outlet"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Consolidated</SelectItem>{outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="pnl-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="pnl-to" />
      </PageHeader>

      {loading || !d ? <Loading /> : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6" data-testid="pnl-statement">
            <span className="eyebrow">Statement</span>
            <div className="mt-2">
              <Row label="Gross Sales" value={d.gross_sales} />
              <Row label="Less: Discounts" value={-d.discounts} indent />
              <Row label="Less: Refunds / Voids" value={-d.refunds} indent />
              <Row label="Net Sales" value={d.net_sales} bold />
              <Row label="Less: COGS" value={-d.cogs} accent="text-amber-600" />
              <Row label={`Gross Profit (${d.gross_margin}%)`} value={d.gross_profit} bold accent="text-emerald-600" />
              <Row label="Less: Operating Expenses" value={-d.operating_expenses} accent="text-amber-600" />
              <div className="mt-2 flex items-center justify-between rounded-sm bg-sidebar px-4 py-4 text-white">
                <span className="font-display font-bold">Net Profit ({d.net_margin}%)</span>
                <span className="font-display text-2xl font-bold tabular">{money(d.net_profit)}</span>
              </div>
            </div>
          </div>

          <div className="rounded-sm border border-border bg-card p-6">
            <span className="eyebrow">Operating Expense Breakdown</span>
            {d.expense_breakdown?.length ? (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart layout="vertical" data={d.expense_breakdown} margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} horizontal={false} />
                  <XAxis type="number" tick={CHART_TICK} tickFormatter={(v) => `${v / 1000}k`} />
                  <YAxis type="category" dataKey="category" tick={CHART_TICK} width={90} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={CHART_TOOLTIP} cursor={CHART_CURSOR} />
                  <Bar dataKey="amount" fill="#64748b" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="mt-6 text-sm text-muted-foreground">No expenses in this period.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
